import { Component, EventEmitter, Input, Output } from '@angular/core';

type DateRange = { start: Date | null; end: Date | null };

@Component({
  selector: 'app-date-range-picker',
  standalone: false,
  templateUrl: './date-range-picker.html',
  styleUrl: './date-range-picker.scss',
})
export class DateRangePicker {
  @Input() start: Date | null = null;
  @Input() end: Date | null = null;
  @Output() rangeChange = new EventEmitter<DateRange>();

  protected open = false;
  protected viewYear: number;
  protected viewMonth: number; // 0-11

  private readonly today = this.stripTime(new Date());

  constructor() {
    const now = new Date();
    this.viewYear = now.getFullYear();
    this.viewMonth = now.getMonth();
  }

  protected toggle() {
    this.open = !this.open;
  }

  protected close() {
    this.open = false;
  }

  protected get displayValue() {
    if (!this.start) return 'Selecione uma data';
    const startText = this.formatDate(this.start);
    const endText = this.end ? this.formatDate(this.end) : startText;
    return `${startText} → ${endText}`;
  }

  protected get monthLabel() {
    const d = new Date(this.viewYear, this.viewMonth, 1);
    return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  protected prevMonth() {
    const prev = new Date(this.viewYear, this.viewMonth - 1, 1);
    this.viewYear = prev.getFullYear();
    this.viewMonth = prev.getMonth();
  }

  protected nextMonth() {
    const next = new Date(this.viewYear, this.viewMonth + 1, 1);
    const max = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    if (next > max) return;
    this.viewYear = next.getFullYear();
    this.viewMonth = next.getMonth();
  }

  protected daysGrid() {
    const first = new Date(this.viewYear, this.viewMonth, 1);
    const startOffset = this.mondayIndex(first); // 0..6
    const lastDay = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const cells: Array<Date | null> = [];

    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let day = 1; day <= lastDay; day++) cells.push(new Date(this.viewYear, this.viewMonth, day));

    const total = Math.ceil(cells.length / 7) * 7;
    while (cells.length < total) cells.push(null);
    return cells;
  }

  protected select(day: Date) {
    if (this.isDisabled(day)) return;

    const d = this.stripTime(day);
    const start = this.start ? this.stripTime(this.start) : null;
    const end = this.end ? this.stripTime(this.end) : null;

    if (!start || end) {
      // Start a new range selection (end is pending).
      this.start = d;
      this.end = null;
      this.rangeChange.emit({ start: d, end: null });
      return;
    }

    if (d < start) {
      this.start = d;
      this.end = start;
      this.rangeChange.emit({ start: d, end: start });
      return;
    }

    this.start = start;
    this.end = d;
    this.rangeChange.emit({ start, end: d });
  }

  protected isDisabled(day: Date) {
    return this.stripTime(day) > this.today;
  }

  protected isInRange(day: Date) {
    if (!this.start || !this.end) return false;
    const d = this.stripTime(day).getTime();
    const start = this.stripTime(this.start).getTime();
    const end = this.stripTime(this.end).getTime();
    return d >= start && d <= end;
  }

  protected isRangeEdge(day: Date) {
    const d = this.stripTime(day);
    return (this.start && this.sameDay(d, this.start)) || (this.end && this.sameDay(d, this.end));
  }

  private stripTime(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private sameDay(a: Date, b: Date) {
    const bb = this.stripTime(b);
    return a.getTime() === bb.getTime();
  }

  private mondayIndex(d: Date) {
    return (d.getDay() + 6) % 7;
  }

  private formatDate(d: Date) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
}
